import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC, s_GB } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

const LETTERS = ['B','C','D','F','G','H','K','L','M','N','P','Q','R','S','T'];
const N = 2; // 2-back
const TOTAL = 20;
const INTERVAL = 2500;

function DualBackGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [phase, setPhase] = useState('start'); // start | playing | done
  const [seq, setSeq] = useState([]); // { pos: 0-8, letter: 'A' }
  const [current, setCurrent] = useState(-1);
  const [posHit, setPosHit] = useState(false);
  const [letHit, setLetHit] = useState(false);
  const [hits, setHits] = useState(0);
  const [total, setTotal] = useState(0);
  const [feedback, setFeedback] = useState(null); // 'pos_correct' | 'let_correct' | 'wrong'
  const intervalRef = useRef(null);
  const seqRef = useRef([]);
  const currentRef = useRef(-1);
  const hitsRef = useRef(0);
  const totalRef = useRef(0);

  const advance = () => {
    const idx = currentRef.current + 1;
    if (idx >= TOTAL) {
      clearInterval(intervalRef.current);
      saveGameScore('dualback', Math.round((hitsRef.current / Math.max(1, totalRef.current)) * 100));
      setPhase('done');
      setHits(hitsRef.current); setTotal(totalRef.current);
      return;
    }
    const item = { pos: Math.floor(Math.random() * 9), letter: LETTERS[Math.floor(Math.random() * LETTERS.length)] };
    seqRef.current = [...seqRef.current, item];
    currentRef.current = idx;
    setCurrent(idx); setSeq([...seqRef.current]);
    setPosHit(false); setLetHit(false); setFeedback(null);
  };

  const startGame = () => {
    seqRef.current = []; currentRef.current = -1; hitsRef.current = 0; totalRef.current = 0;
    setSeq([]); setCurrent(-1); setHits(0); setTotal(0); setFeedback(null);
    setPhase('playing');
    advance();
    intervalRef.current = setInterval(advance, INTERVAL);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handlePos = () => {
    if (currentRef.current < N || posHit) return;
    const idx = currentRef.current;
    const match = seqRef.current[idx].pos === seqRef.current[idx - N].pos;
    totalRef.current++;
    if (match) { hitsRef.current++; setFeedback('pos_correct'); } else { setFeedback('wrong'); }
    setPosHit(true);
  };

  const handleLet = () => {
    if (currentRef.current < N || letHit) return;
    const idx = currentRef.current;
    const match = seqRef.current[idx].letter === seqRef.current[idx - N].letter;
    totalRef.current++;
    if (match) { hitsRef.current++; setFeedback('let_correct'); } else { setFeedback('wrong'); }
    setLetHit(true);
  };

  const item = seq[current];
  const posInGrid = item?.pos ?? -1;

  if (phase === 'done') {
    return (
      <div>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>
        <div style={{ ...s_GC({ padding: 24, textAlign: 'center' }) }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🧠</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Klaar!' : 'Done!'}</div>
          <div style={{ color: _T.accent, fontSize: 32, fontWeight: 700 }}>{hits}/{total}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 16 }}>{nl ? 'correcte N-back matches' : 'correct N-back matches'}</div>
          <button style={s_PB({ width: '100%' })} onClick={startGame}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>

      {phase === 'start' && (
        <div style={{ ...s_GC({ padding: 20, marginBottom: 14 }) }}>
          <div style={{ color: C.white, fontWeight: 700, marginBottom: 8 }}>🧠 {nl ? '2-Back geheugentraining' : '2-Back Memory Training'}</div>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6 }}>
            {nl ? `Elke ronde zie je een letter en een positie. Druk "Positie" als de huidige positie overeenkomt met die van ${N} rondes geleden. Druk "Letter" als de letter overeenkomt.` : `Each round shows a letter and position. Press "Position" if the current position matches the one from ${N} rounds ago. Press "Letter" if the letter matches.`}
          </div>
        </div>
      )}

      {phase === 'playing' && item && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ color: C.silver, fontSize: 12 }}>{current + 1}/{TOTAL}</span>
            <span style={{ color: feedback === 'pos_correct' || feedback === 'let_correct' ? C.teal : feedback === 'wrong' ? C.red : C.silver, fontSize: 12, fontWeight: 700 }}>
              {feedback === 'pos_correct' ? '✅ Positie!' : feedback === 'let_correct' ? '✅ Letter!' : feedback === 'wrong' ? '❌ Fout!' : ''}
            </span>
          </div>

          {/* 3x3 position grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, maxWidth: 200, margin: '0 auto 14px', padding: 10, background: _T.bg4, borderRadius: 14 }}>
            {Array.from({ length: 9 }, (_, i) => (
              <div key={i} style={{ paddingTop: '100%', borderRadius: 10, position: 'relative', background: i === posInGrid ? '#7B6FD0' : '#1a2a4a' }}>
                {i === posInGrid && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: 18, fontWeight: 700 }}>{item.letter}</div>}
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button onClick={handlePos} disabled={posHit || current < N}
              style={{ ...s_GB({ padding: '16px 0', fontSize: 13, width: '100%' }), opacity: posHit || current < N ? 0.4 : 1 }}>
              📍 {nl ? 'Positie' : 'Position'}
            </button>
            <button onClick={handleLet} disabled={letHit || current < N}
              style={{ ...s_GB({ padding: '16px 0', fontSize: 13, width: '100%' }), opacity: letHit || current < N ? 0.4 : 1 }}>
              🔤 {nl ? 'Letter' : 'Letter'}
            </button>
          </div>
          {current < N && <div style={{ color: C.silver, fontSize: 11, textAlign: 'center', marginTop: 8 }}>{nl ? `Wacht op ${N + 1} rondes...` : `Wait for ${N + 1} rounds...`}</div>}
        </>
      )}

      {phase === 'start' && <button style={s_PB({ width: '100%', marginTop: 8 })} onClick={startGame}>{nl ? 'Start' : 'Start'}</button>}
    </div>
  );
}

export default DualBackGame;
